import type {
  LocationsApiGetLocationsRequest as LocationsFilterParams,
  LocationsOutput as LocationsResponse,
  LocationsInput as LocationCreateParams,
  PartialLocationUpdateDto as LocationUpdateParams,
  AddressesApiValidateAddressRequest as ValidateRequest,
  DocumentsApiGetDocumentsRequest as DocumentFilterParams,
  DocumentsOutput as DocumentsResponse,
  ValidateAddressResponse,
  CountriesOutput,
  CountryOutput,
  DocumentsApiCreateResaleCertificateRequest as GenerateCertificateParams,
  FullUpdateDto as DocumentCreateParams,
  LocationModel,
} from '@b-stock/location-api-client'

import appConfig from '@config/config'
import { deleteReq, get, patch, post } from '@helpers/xhr'

const { api } = appConfig

type UpdatedLocation = {
  id: string
  data: LocationUpdateParams
  originalId?: string
}

type LocationUpdateErrorBody = {
  statusCode?: number
  messages?: string
}

// `updateAddress`'s recovery path keys off the shape of the error body, not
// axios's `isAxiosError` marker — callers (and tests) reject with plain objects.
const getUpdateErrorBody = (
  e: unknown
): LocationUpdateErrorBody | undefined => {
  if (typeof e !== 'object' || e === null || !('response' in e)) {
    return undefined
  }
  const { response } = e
  if (typeof response !== 'object' || response === null) {
    return undefined
  }
  return 'data' in response
    ? (response.data as LocationUpdateErrorBody)
    : undefined
}

// This is terrible, remove it when we get our lives together on
// country/province codes
const upperProvinceCountryCodes = <
  T extends { countryCode?: string; provinceCode?: string },
>({
  countryCode,
  provinceCode,
  ...rest
}: T): T =>
  // Not ideal, but the existence of this function at all is not ideal either
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  ({
    ...rest,
    countryCode: countryCode?.toUpperCase(),
    ...(provinceCode && { provinceCode: provinceCode.toUpperCase() }),
  }) as T

export default {
  async getAddress({ id }: { id: string }) {
    const { data } = await get<LocationModel>(api.locations.id.default, null, {
      entities: { id },
    })
    return data
  },
  async fetchAddresses({ address }: { address: LocationsFilterParams }) {
    const { data } = await get<LocationsResponse>(api.locations.default, {
      limit: 100,
      ...upperProvinceCountryCodes(address),
    })

    return data
  },
  async createAddress(data: LocationCreateParams) {
    const { data: response } = await post<LocationModel>(
      api.locations.default,
      upperProvinceCountryCodes(data)
    )
    return response
  },
  // Returns `undefined` when the patch fails for a reason the `isActive`
  // recovery below doesn't handle — the catch deliberately swallows it.
  async updateAddress({
    id,
    data,
    originalId,
  }: UpdatedLocation): Promise<LocationModel | undefined> {
    const upperCaseProvinceCountryCodes = upperProvinceCountryCodes(data)
    try {
      const { data: response } = await patch<LocationModel>(
        api.locations.id.default,
        upperCaseProvinceCountryCodes,
        { entities: { id } }
      )
      return response
    } catch (e: unknown) {
      const errorData = getUpdateErrorBody(e)
      if (errorData) {
        // TODO - we will be checking the error code when backend start sending error code - Ticket needs to be created
        // The reason behind checking for the isActive message is because when we are following non linear navigation
        // the location id which was active during first time will be inactive and new location id will be created but
        // original id will be same - so we will be fetching the active address for given originalId and
        // since there will always be one active address we will be grabing that location id
        // and patching that
        if (
          errorData.statusCode === 403 &&
          errorData.messages ===
            'Cannot update a location that has `isActive` set to false' &&
          originalId
        ) {
          const { data: locationsData } = await get<LocationsResponse>(
            api.locations.default,
            {
              limit: 1,
              originalId,
            }
          )

          if (locationsData.locations[0]) {
            const { data: response } = await patch<LocationModel>(
              api.locations.id.default,
              upperCaseProvinceCountryCodes,
              { entities: { id: locationsData.locations[0].id } }
            )
            return response
          }
        }
      }
    }
  },
  async deleteAddress({ id }: { id: string }) {
    const { data: response } = await deleteReq<never>(
      api.locations.id.default,
      undefined,
      {
        entities: { id },
      }
    )
    return response
  },
  async validateAddress(
    payload: ValidateRequest
  ): Promise<ValidateAddressResponse> {
    const { data } = await get<ValidateAddressResponse>(
      api.locations.validate,
      upperProvinceCountryCodes(payload)
    )
    return data
  },
  async fetchCountryList() {
    const { data } = await get<Record<string, CountriesOutput>>(
      api.locations.countries
    )
    return data
  },
  async fetchDocs(payload: DocumentFilterParams) {
    const { data } = await get<DocumentsResponse>(
      api.locations.document,
      payload
    )
    return data
  },
  async uploadBusinessLicenseDocument(data: DocumentCreateParams) {
    const response = await post<DocumentCreateParams>(
      api.locations.document,
      data
    )
    return response
  },
  async fetchNationalExemptionReqs(country: string) {
    const { data } = await get<CountryOutput>(
      api.locations.exemptionReqs.country,
      null,
      {
        entities: { country },
      }
    )
    return data
  },
  async generateResaleCertificate(
    image: Blob | null,
    query: GenerateCertificateParams
  ) {
    const formData = new FormData()
    if (image) {
      formData.append('image', image)
    }
    // Requested as a blob; the generated client types this endpoint `void`.
    const { data } = await post<Blob>(
      api.locations.generateResaleCertificate,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: query,
        responseType: 'blob',
      }
    )
    return data
  },
}
